import {ResponsiveLine} from "@nivo/line";
import {scaleTime} from "d3-scale";
import {Bin} from "d3-array";
import {TimeRange, QueueCall} from "./types";
import {makeStyles} from "tss-react/mui";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";

const useStyles = makeStyles()({
    graphContainer: {
        width: 800,
        height: 400,
    },
});

const graphTimeScales: {[key in TimeRange]: {precision: string; ticks: string}} = {
    [TimeRange.Today]: {
        precision: "minute",
        ticks: "every 2 hour",
    },
    [TimeRange.Yesterday]: {
        precision: "minute",
        ticks: "every 2 hour",
    },
    [TimeRange.ThisWeek]: {
        precision: "hour",
        ticks: "every 12 hours",
    },
    [TimeRange.PreviousWeek]: {
        precision: "hour",
        ticks: "every 12 hours",
    },
    [TimeRange.Month]: {
        precision: "day",
        ticks: "every 2 days",
    },
};

type ValueFormatFunction = (value: any) => string;

interface CallGraphSettings {
    valueLabel: MessageDescriptor;
    valueFormat: ValueFormatFunction;
    valueLegendFormat: ValueFormatFunction;
    valueLegendOffset: number;
}

export const callCountGraph: CallGraphSettings = {
    valueLabel: defineMessage({id: "call_queue.statistics.graph.calls", defaultMessage: "Calls"}),
    valueFormat: (value) => `${value}`,
    valueLegendFormat: (value) => `${value}`,
    valueLegendOffset: -40,
};

export const callDurationGraph: CallGraphSettings = {
    valueLabel: defineMessage({id: "call_queue.statistics.graph.call-duration", defaultMessage: "Duration"}),
    valueFormat: (value) => `${value.toFixed(2)} s`,
    valueLegendFormat: (value) => `${value} s`,
    valueLegendOffset: -50,
};

export type GraphMappings = {
    [label: string]: (calls: QueueCall[]) => number;
};

type SerieArray = React.ComponentProps<typeof ResponsiveLine>["data"];

function prepareGraphData(binnedData: Bin<QueueCall, Date>[], mapping: GraphMappings): SerieArray {
    return Object.entries(mapping).map(([label, mapFN]) => ({
        id: label,
        data: binnedData.map((d) => ({x: d.x0, y: mapFN(d)})),
    }));
}

type Props = {
    graphType: CallGraphSettings;
    data: Bin<QueueCall, Date>[];
    mapping: GraphMappings;
    timeRange: TimeRange;
};

export default (props: Props) => {
    const {classes} = useStyles();
    const data = prepareGraphData(props.data, props.mapping);

    return (
        <div className={classes.graphContainer}>
            <ResponsiveLine
                data={data}
                margin={{top: 50, right: 110, bottom: 50, left: 60}}
                xScale={{
                    type: "time",
                    format: "%Y-%m-%d",
                    useUTC: false,
                    // precision: graphTimeScales[timeRange].precision,
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{
                    type: "linear",
                    stacked: false,
                }}
                yFormat={props.graphType.valueFormat}
                axisLeft={{
                    legend: <FormattedMessage {...props.graphType.valueLabel} />,
                    legendOffset: props.graphType.valueLegendOffset,
                    legendPosition: "middle",
                    format: props.graphType.valueLegendFormat,
                }}
                axisBottom={{
                    format: scaleTime().tickFormat(),
                    tickValues: graphTimeScales[props.timeRange].ticks,
                    legend: <FormattedMessage id="call_queue.statistics.graph.time" defaultMessage="Time" />,
                    legendOffset: 36,
                    legendPosition: "middle",
                }}
                colors={{scheme: "category10"}}
                // tooltip shows values for all the lines at that x
                enableSlices="x"
                enablePoints={false}
                theme={{
                    tooltip: {
                        container: {
                            whiteSpace: "nowrap",
                            fontSize: 10,
                        },
                    },
                }}
            />
        </div>
    );
};
